import React from 'react';
import { withAuthenticationRequired, useAuth0 } from '@auth0/auth0-react';
import {
  Box, Button, Flex, Heading, Text, useColorModeValue,
} from '@chakra-ui/react';
import {
  FiArrowUpRight, FiFileText, FiLayers, FiTool, FiGlobe, FiFolder, FiAlertCircle,
} from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { NIL } from 'uuid';
import useDashboardMetrics from '../hooks/useDashboardMetrics';
import useCurrentUser from '../hooks/useCurrentUser';
import { compactMoney } from '../utils/money';
import AuroraBackground from '../shared/dashboard/AuroraBackground';
import { useAppearance } from '../theme/AppearanceProvider';
import WorkingOnCard, { WorkingOnItem } from '../shared/dashboard/WorkingOnCard';
import ActivityFeedCard, { ActivityItem } from '../shared/dashboard/ActivityFeedCard';
import SalesBibleCard from '../shared/dashboard/SalesBibleCard';
import ProjectsLedgerCard from '../shared/dashboard/ProjectsLedgerCard'; // item 603
import TeamChatCard from '../shared/dashboard/TeamChatCard';
import RecurringSubscriptionsCard from '../shared/dashboard/RecurringSubscriptionsCard'; // dart NEXT_EPIC #4
import AuditLogCard from '../shared/dashboard/AuditLogCard'; // item 1044 — the 351 audit feed as a universe card
import DraggableCards from '../shared/DraggableCards'; // item 604/605

// row icon per kind for the active-work + activity lists
const KIND_META: Record<string, { icon: React.ReactNode, accent: string }> = {
  proposal: { icon: <FiLayers />, accent: 'brandBlue.300' },
  invoice: { icon: <FiFileText />, accent: 'brandGreen.400' },
  workOrder: { icon: <FiTool />, accent: 'brandGreen.400' },
};

// The landing (item 96): the always-visible KPI strip is rendered above by the shell
// (App.tsx) on EVERY page; this route is the "what you're working on" active-work
// region — the default home content. The full bento lives behind the strip's expand.
const DashboardPage: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth0();
  const m = useDashboardMetrics();
  const { hasFinancialAccess, isAdmin, isSuperAdmin } = useCurrentUser();
  const showAudit = isAdmin || isSuperAdmin; // item 1044 — the audit card is admin-only

  const firstName = user?.given_name ?? user?.name?.split(' ')[0] ?? 'there';
  // role-named hero (Chad): money-facing seats see the whole "Universe", others the
  // project-only view. Defaults to Universe until roles are enforced (item 34).
  const dashTitle = hasFinancialAccess ? 'Universe Dashboard' : 'Project Dashboard';
  const dashIcon = hasFinancialAccess ? <FiGlobe /> : <FiFolder />;
  const { aurora } = useAppearance();
  const chipBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');
  // item 155 — lift the hero subtitle off the brand glow for a readable contrast floor
  const subColor = useColorModeValue('gray.700', 'whiteAlpha.800');
  const greetColor = useColorModeValue('gray.800', 'whiteAlpha.900');
  const greetGradient = useColorModeValue(
    'linear(to-r, gray.800, brandGreen.500)',
    'linear(to-r, white, brandGreen.300)',
  );
  // item 468 — past-due widget palette (hoisted: hooks can't be called conditionally)
  const pastDueBorder = useColorModeValue('red.300', 'red.500');
  const pastDueBg = useColorModeValue('red.50', 'rgba(229,62,62,0.12)');
  const pastDueText = useColorModeValue('red.700', 'red.200');

  return (
    // bleed past the shell's content gutters so the aurora fills the viewport
    <Box
      position="relative"
      mx={{ base: -4, md: -10 }}
      mb={{ base: -4, md: -8 }}
      px={{ base: 4, md: 10 }}
      pt={{ base: 4, md: 6 }}
      pb={{ base: 6, md: 10 }}
      minH={{ base: 'auto', lg: 'calc(100vh - 160px)' }} // item 1063
    >
      <AuroraBackground />

      <Box position="relative" zIndex={1}>
        <Flex
          direction={{ base: 'column', md: 'row' }}
          align={{ base: 'flex-start', md: 'flex-end' }}
          justify="space-between"
          mb={{ base: 6, md: 8 }}
          gap={3}
        >
          <Flex align="center" gap={{ base: 6, md: 10 }} minW={0}>
            <Flex
              w={{ base: '48px', md: '56px' }}
              h={{ base: '48px', md: '56px' }}
              flexShrink={0}
              align="center"
              justify="center"
              // explicit margin (Chakra 1.6 doesn't reliably apply `gap` here) so the
              // title is always clearly clear of the icon
              mr={{ base: 5, md: 8 }}
              borderRadius={aurora ? 'xl' : 'md'}
              bg={chipBg}
              color={aurora ? 'brandGreen.400' : greetColor}
              fontSize={{ base: '26px', md: '30px' }}
              borderWidth="1px"
              borderColor={aurora ? 'brandGreen.400' : 'whiteAlpha.300'}
              // contained: accent ring + INSET glow — no outward halo bleeding into
              // the title (matches the shared PageHeader chip, item 105)
              boxShadow={aurora ? 'inset 0 0 18px -8px var(--chakra-colors-brandGreen-400)' : 'none'}
            >
              {dashIcon}
            </Flex>
            <Box minW={0}>
              <Heading
                size="2xl"
                letterSpacing="-0.03em"
                lineHeight="1.05"
                color={aurora ? undefined : greetColor}
                bgGradient={aurora ? greetGradient : undefined}
                bgClip={aurora ? 'text' : undefined}
              >
                {dashTitle}
              </Heading>
              <Text mt={2} color={subColor}>
                {`Welcome back, ${firstName} — here's what needs you.`}
              </Text>
            </Box>
          </Flex>
          {/* primary CTA respects the role-dynamic dashboard (item 102): money seats
              lead with New Proposal, project-only roles lead with Add Project */}
          <Button
            rightIcon={<FiArrowUpRight />}
            onClick={() => history.push(hasFinancialAccess ? `/proposal/${NIL}` : `/project/edit/${NIL}`)}
            alignSelf={{ base: 'stretch', md: 'auto' }}
          >
            {hasFinancialAccess ? 'New Proposal' : 'Add Project'}
          </Button>
        </Flex>

        {/* item 468 — Past Due: a compact, actionable red widget that appears only when
            money-facing users have invoices past their Net-N due date. Click → AR report. */}
        {hasFinancialAccess && m.pastDueCount > 0 && (
          <Flex
            role="button"
            tabIndex={0}
            onClick={() => history.push('/reports#ar-report')}
            onKeyDown={(e) => { if (e.key === 'Enter') history.push('/reports#ar-report'); }}
            align="center"
            wrap="wrap"
            // Chakra 1.6 ignores the `gap` prop — sx works (the banner rendered crammed)
            sx={{ gap: '12px' }}
            mb={4}
            px={4}
            py={3}
            borderRadius="lg"
            borderWidth="1px"
            borderColor={pastDueBorder}
            bg={pastDueBg}
            cursor="pointer"
            _hover={{ borderColor: 'red.400' }}
          >
            <Box color="red.400" fontSize="lg"><FiAlertCircle /></Box>
            <Text fontSize="sm" fontWeight="semibold" color={pastDueText}>
              {`${m.pastDueCount} ${m.pastDueCount === 1 ? 'invoice is' : 'invoices are'} past due`}
            </Text>
            <Text fontSize="sm" color={subColor}>
              {`${compactMoney(m.pastDueTotal)} outstanding past its due date`}
            </Text>
            <Box ml="auto" color={subColor}><FiArrowUpRight /></Box>
          </Flex>
        )}

        {/* item 604 — the universe cards are now GRAB-A-HANDLE drag-and-drop reorderable
            (hover a card → grip handle top-right → drag from the handle only, so the card's
            own search/scroll/clicks are never hijacked); the arrangement persists per-device.
            item 605 — DEFAULT order: Projects ledger first, then Sales Bible, then the rest
            (Team Chat · Activity · Working On). A saved drag-layout overrides this order. */}
        <DraggableCards
          storageKey="basix.universeCards.v1"
          cards={[
            // item 603 — projects ledger: every project in one searchable/sortable list
            { id: 'projects', label: 'Projects', node: <ProjectsLedgerCard /> },
            // Sales Bible — auto-populated job tracker (item 177), visible to ALL roles
            { id: 'salesBible', label: 'Sales Bible', node: <SalesBibleCard /> },
            // dart (2026-07-15, Chad) — NEXT_EPIC #4: recurring-subscriptions roster (money view only)
            ...(hasFinancialAccess ? [{ id: 'recurring', label: 'Recurring', node: <RecurringSubscriptionsCard /> }] : []),
            // item 1044 (Chad) — the 351 audit log as a universe card (was live-dash-only)
            ...(showAudit ? [{ id: 'auditLog', label: 'Audit Log', node: <AuditLogCard /> }] : []),
            // full-width global team chat (item 217), open to ALL roles
            { id: 'teamChat', label: 'Team Chat', node: <TeamChatCard /> },
            {
              id: 'activity',
              label: 'Activity',
              node: (
                <ActivityFeedCard
                  items={m.activity.map((a): ActivityItem => ({
                    id: a.id,
                    label: a.label,
                    sub: a.sub,
                    when: a.when,
                    href: a.href,
                    icon: KIND_META[a.kind].icon,
                    accent: KIND_META[a.kind].accent,
                  }))}
                  onOpen={(href) => history.push(href)}
                />
              ),
            },
            {
              id: 'workingOn',
              label: 'Working On',
              node: (
                <WorkingOnCard
                  items={m.actions.map((a): WorkingOnItem => ({
                    id: a.id,
                    label: a.label,
                    sub: a.sub,
                    note: a.note,
                    href: a.href,
                    icon: KIND_META[a.kind].icon,
                    accent: KIND_META[a.kind].accent,
                  }))}
                  onOpen={(href) => history.push(href)}
                />
              ),
            },
          ]}
        />
      </Box>
    </Box>
  );
};

export default withAuthenticationRequired(DashboardPage);
